import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** localStorage wrapper that silently no-ops in restricted browsers. */
const safeLocalStorage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = {
  getItem: (name) => {
    try { return localStorage.getItem(name); } catch { return null; }
  },
  setItem: (name, value) => {
    try { localStorage.setItem(name, value); } catch { /* noop */ }
  },
  removeItem: (name) => {
    try { localStorage.removeItem(name); } catch { /* noop */ }
  },
};

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ShippingAddress {
  fullName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface CartItemBase {
  cartItemId: string;
  qty: number;
  price: number;
  content?: { image?: string; name?: string };
}

export interface ArtworkCartItem extends CartItemBase {
  kind: "artwork";
  artId: string;
  index: number;
}

export interface MerchCartItem extends CartItemBase {
  kind: "merch";
  artId: string;
  index: number;
  variantId: string | number;
}

export interface PosterTemplateCartItem extends CartItemBase {
  kind: "posterTemplate";
  templateId: string;
  templateSlug: string;
  selectedSizeCode: string;
  selectedVariantSku?: string;
  personalizationUploadId?: string;
  personalizationSourceUrl?: string;
}

export type CartItem = ArtworkCartItem | MerchCartItem | PosterTemplateCartItem;

/* ------------------------------------------------------------------ */
/*  Promo helpers                                                      */
/* ------------------------------------------------------------------ */

const PROMOS: Record<
  string,
  { discountPercent: number; appliesTo: CartItem["kind"][] }
> = {
  GHIBGEN20: { discountPercent: 20, appliesTo: ["artwork", "merch", "posterTemplate"] },
  FREEGHIB25: { discountPercent: 100, appliesTo: ["artwork"] },
};

function getPromoDiscount(cart: CartItem[], code: string | null): number {
  if (!code) return 0;
  const promo = PROMOS[code.toUpperCase()];
  if (!promo) return 0;
  return cart.reduce((sum, item) => {
    if (!promo.appliesTo.includes(item.kind)) return sum;
    return sum + item.price * item.qty * (promo.discountPercent / 100);
  }, 0);
}

export function isValidPromoCode(code: string): boolean {
  return !!PROMOS[code.toUpperCase()];
}

/* ------------------------------------------------------------------ */
/*  Store                                                              */
/* ------------------------------------------------------------------ */

interface CartState {
  items: CartItem[];
  promoCode: string | null;
  address: ShippingAddress | null;
  gift: { enabled: boolean; subject: string; message: string } | null;
  drawerOpen: boolean;

  // Actions
  addItem: (item: CartItem) => void;
  removeItem: (cartItemId: string) => void;
  updateQty: (cartItemId: string, qty: number) => void;
  clearCart: () => void;
  setPromoCode: (code: string | null) => void;
  setAddress: (address: ShippingAddress | null) => void;
  setGift: (gift: { enabled: boolean; subject: string; message: string } | null) => void;
  openDrawer: () => void;
  closeDrawer: () => void;

  // Computed
  subtotal: () => number;
  discountAmount: () => number;
  discountedSubtotal: () => number;
  itemCount: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      promoCode: null,
      address: null,
      gift: null,
      drawerOpen: false,

      addItem: (item) =>
        set((s) => {
          // If same template + size + variant + personalization already in cart, increment qty
          const existing = s.items.find((i) => {
            if (i.kind === "posterTemplate" && item.kind === "posterTemplate") {
              return (
                i.templateId === item.templateId &&
                i.selectedSizeCode === item.selectedSizeCode &&
                i.selectedVariantSku === item.selectedVariantSku &&
                (i.personalizationUploadId ?? "") === (item.personalizationUploadId ?? "")
              );
            }
            if (i.kind === "merch" && item.kind === "merch") {
              return i.variantId === item.variantId;
            }
            if (i.kind === "artwork" && item.kind === "artwork") {
              return i.artId === item.artId && i.index === item.index;
            }
            return false;
          });
          if (existing) {
            return {
              items: s.items.map((i) =>
                i.cartItemId === existing.cartItemId
                  ? { ...i, qty: i.qty + item.qty }
                  : i,
              ),
            };
          }
          return { items: [...s.items, item] };
        }),

      removeItem: (cartItemId) =>
        set((s) => ({ items: s.items.filter((i) => i.cartItemId !== cartItemId) })),

      updateQty: (cartItemId, qty) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.cartItemId === cartItemId ? { ...i, qty: Math.max(1, qty) } : i,
          ),
        })),

      clearCart: () => set({ items: [], promoCode: null, gift: null }),

      setPromoCode: (code) => {
        if (code && !PROMOS[code.toUpperCase()]) return;
        set({ promoCode: code ? code.toUpperCase() : null });
      },

      setAddress: (address) => set({ address }),
      setGift: (gift) => set({ gift }),
      openDrawer: () => set({ drawerOpen: true }),
      closeDrawer: () => set({ drawerOpen: false }),

      subtotal: () => get().items.reduce((sum, i) => sum + i.price * i.qty, 0),
      discountAmount: () => getPromoDiscount(get().items, get().promoCode),
      discountedSubtotal: () => get().subtotal() - get().discountAmount(),
      itemCount: () => get().items.reduce((sum, i) => sum + i.qty, 0),
    }),
    {
      name: "ghibgen-cart",
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        items: state.items,
        promoCode: state.promoCode,
        address: state.address,
        gift: state.gift,
      }),
    },
  ),
);
